import { alpha, Tooltip } from '@mui/material';
import React, { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { PermissionsGroupId } from '../../../API';
import { useTheme } from '../../../hooks';
import { permissionsGroupBadgeTestIds } from '../TestsIds';

export type PermissionsGroupBadgeProps = {
  values: PermissionsGroupId[];
  className?: string;
};

const useStyles = makeStyles<{ color: 'primary' | 'secondary' }>()((theme, props) => {
  const x = theme.palette.mode === 'dark' ? 2 : 1;
  return {
    badge: {
      borderRadius: 3,
      padding: '3px 5px',
      border: `1px solid ${theme.palette[props.color].light}`,
      fontSize: 10,
      borderLeftWidth: 3,
      letterSpacing: '0.75px',
      cursor: 'default',
      flex: 'none',
      background: `linear-gradient(180deg, transparent 0%, ${alpha(
        theme.palette[props.color].main,
        0.2 * x
      )} 39%, ${alpha(theme.palette[props.color].main, 0.4 * x)} 40%, ${alpha(
        theme.palette[props.color].main,
        0.2 * x
      )} 100%)`
    }
  };
});

export function PermissionsGroupBadge({ values, className }: PermissionsGroupBadgeProps): React.ReactElement {
  const { lowerContrastColor } = useTheme();
  const { cx, classes } = useStyles({ color: lowerContrastColor });

  const TooltipComponent = useMemo(() => {
    const Component = ({ children }: { children: React.ReactNode }) =>
      values.length > 1 ? (
        <Tooltip title={values.join(', ')} placement="top">
          {children as React.ReactElement}
        </Tooltip>
      ) : (
        <>{children}</>
      );
    return Component;
  }, [values]);

  if (values.length === 0) {
    return <></>;
  }

  return (
    <TooltipComponent>
      <div className={cx(className, classes.badge)} data-testid={permissionsGroupBadgeTestIds.badge}>
        {values[0]} {values.length > 1 && `+${values.length - 1}`}
      </div>
    </TooltipComponent>
  );
}
